import React, { useState, useEffect } from "react";
import { useSpring, animated } from "@react-spring/web";
import {
  Typography,
  Grid,
  Box,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { request } from "../../../interfaces/networkManager";
import Error from "../../../components/Error/Error";
import UserDetails from "../components/UserDetails";
import { getCurrentUrl } from "../../../services/ApiServices";
import PropspectsTable from "../components/ProspectsTable";
import { CustomPaper } from "../styles";
import { Dayjs } from "dayjs";
import { headers } from "../../../services/genericConstantes";
import { UserDTO } from "../../../models/userDTO";
import UserConnections from "../components/UserConnections";
import { Invoice } from "../../../models/invoice";

interface GenericInformationsDTO {
  countTechnicians: number;
  countBreeders: number;
  countProspects: number;
  countProspectsInDiscoverOffer: number;
  countTechniciansWithPhoto: number;
}

const styleChart = {
  [`& .${pieArcLabelClasses.root}`]: {
    fill: "white",
    fontWeight: "bold",
  },
};

const colors = ["#F79F79", "#7BE0AD", "#66C3FF"];

const genericSeries: object = {
  highlightScope: { faded: "global", highlighted: "item" },
  faded: { innerRadius: 30, additionalRadius: -30 },
  arcLabel: (item: any) => item.value.toString(),
  innerRadius: 40,
  arcLabelMinAngle: 45,
  outerRadius: 100,
  cornerRadius: 5,
  cx: 100,
};

const UsersPart = () => {
  const [genericInformations, setGenericInformations] =
    useState<GenericInformationsDTO | null>(null);
  const [searchResults, setSearchResults] = useState<UserDTO[]>([]);
  const [user, setUser] = useState<UserDTO | null>(null);
  const [lastinvoicesUser, setLastInvoicesUser] = useState<Invoice | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [openMessage, setOpenMessage] = useState<string | null>(null);
  const [chartAnimation, setChartAnimation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const datasForChartStatsUsers = [
    {
      id: 0,
      value: genericInformations?.countTechnicians || 0,
      label: "NB techniciens",
    },
    {
      id: 1,
      value: genericInformations?.countBreeders || 0,
      label: "NB éleveurs",
    },
    {
      id: 2,
      value: genericInformations?.countProspects || 0,
      label: "NB prospects",
    },
  ];
  const datasForChartProspects = [
    {
      id: 0,
      value: genericInformations
        ? genericInformations.countProspects -
          genericInformations.countProspectsInDiscoverOffer
        : 0,
      label: "Non",
    },
    {
      id: 1,
      value: genericInformations?.countProspectsInDiscoverOffer || 0,
      label: "Oui",
    },
  ];
  const datasForChartTechnicians = [
    {
      id: 0,
      value: genericInformations
        ? genericInformations.countTechnicians -
          genericInformations.countTechniciansWithPhoto
        : 0,
      label: "Non",
    },
    {
      id: 1,
      value: genericInformations?.countTechniciansWithPhoto || 0,
      label: "Oui",
    },
  ];

  useEffect(() => {
    request<GenericInformationsDTO>("/users/general-informations")
      .then((response: GenericInformationsDTO) => {
        if (!response.countBreeders)
          return setError(
            "Erreur lors de la récupération des informations générales"
          );
        setGenericInformations(response);
        setChartAnimation(true);
      })
      .catch((error) => {
        setError("Erreur lors de la récupération des informations générales");
      });
  }, []);

  /**
   * Quand la valeur dans le TextField change, on fait une requête pour récupérer les utilisateurs correspondants
   * @param event
   */
  const handleInputChange = async (event: any) => {
    if (event?.target) {
      const { value } = event.target;
      if (value?.length >= 3) {
        setSearchTerm(value);
        await request<any>(
          `/phmanager/autoCompleteUserInput/${
            value ? `?searchText=${value}` : null
          }`
        )
          .then((response: UserDTO[]) => {
            setSearchResults(response);
          })
          .catch((error) => {
            setError("Erreur lors de la récupération des utilisateurs");
          });
      }
    }
  };

  const onClickUser = async (value: string) => {
    const userLoginAndDisplayName = value.split(" / ")[0];
    const userLogin = userLoginAndDisplayName.split("--> Cheptel : ")[1];
    const userToSaved =
      searchResults.find((user) => user.userLogin === userLogin) || null;
    if (userToSaved) {
      userToSaved.password = "";
      if (
        userToSaved?.breeder !== null &&
        userToSaved?.breeder.farms.length > 0
      ) {
        await request<any>(
          `/invoices/farms/lastInvoice/${userToSaved?.breeder.farms[0].idOrganisation}`
        ).then((response) => {
          if (response) setLastInvoicesUser(response);
        });
      }
    }
    setUser(userToSaved);
    setSearchTerm(value);
    // Fermer la liste déroulante
    const input = document.getElementById("search-autocomplete");
    if (input) {
      input.blur();
    }
  };

  /**
   * Envoie les modifications de l'utilisateur au serveur
   * @param userToUpdate
   * @param beginOfferDate
   * @param endOfferDate
   */
  const postUpdatedUser = (
    userToUpdate: UserDTO,
    beginOfferDate: Dayjs | null,
    endOfferDate: Dayjs | null
  ) => {
    if (
      userToUpdate.role.code === "EL_PROSPECT" &&
      beginOfferDate &&
      endOfferDate
    ) {
      userToUpdate.discoveryOfferActivationDate =
        beginOfferDate.format("YYYY-MM-DD");
      userToUpdate.discoveryOfferClosingDate =
        endOfferDate.format("YYYY-MM-DD");
    }

    const postOptions = {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify(userToUpdate),
    };

    fetch(`${getCurrentUrl()}/users/${userToUpdate.idPerson}`, postOptions)
      .then(async (response) => {
        if (response.ok) {
          const updatedUser = await response.json();
          if (updatedUser?.idPerson) {
            updatedUser.password = userToUpdate.password;
            setUser(updatedUser);
            setOpenMessage("Vos modifications ont bien été enregistrées !");
          } else {
            setError(
              "Erreur: la réponse ne ressemble pas à un objet Utilisateur"
            );
          }
        } else {
          setError("Erreur lors de la mise à jour de l'utilisateur");
        }
      })
      .catch((error) => {
        setOpenMessage(
          "Erreur lors de la mise à jour de l'utilisateur, \nSi vous avez modifier l'image de l'utilisateur, c'est que l'image est trop lourde"
        );
      });
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenMessage(null);
  };

  /**
   * Animation des charts
   */
  const chartSpring = useSpring({
    opacity: chartAnimation ? 1 : 0,
    transform: chartAnimation ? "translateX(0px)" : "translateX(-100px)",
    config: { duration: 500 },
  });

  return (
    <div>
      <Snackbar
        open={!!openMessage}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openMessage?.includes("Erreur") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {openMessage}
        </Alert>
      </Snackbar>
      <Grid container>
        <Grid size={12}>
          <CustomPaper>
            <Typography variant="h5" color="primary" gutterBottom align="left">
              Informations générales :
            </Typography>
            {error && <Error message={error} />}
            {genericInformations ? (
              <Grid
                container
                sx={{ spacing: 2, justifyContent: "space-evenly" }}
              >
                <Grid size={{ sm: 12, lg: 3.5 }} flexGrow={1}>
                  <Typography variant="body2" gutterBottom align="center">
                    Tous les utilisateurs par type dans notre base :
                  </Typography>
                  <animated.div style={chartSpring}>
                    <PieChart
                      colors={colors}
                      series={[
                        {
                          data: datasForChartStatsUsers,
                          ...genericSeries,
                        },
                      ]}
                      sx={styleChart}
                      height={200}
                    />
                  </animated.div>
                </Grid>
                <Grid size={{ sm: 12, lg: 3 }} flexGrow={1}>
                  <Typography variant="body2" gutterBottom align="center">
                    Ont-ils déjà souscrit à l'offre découverte ?
                  </Typography>
                  <animated.div style={chartSpring}>
                    <PieChart
                      colors={colors}
                      series={[
                        {
                          data: datasForChartProspects,
                          ...genericSeries,
                        },
                      ]}
                      sx={styleChart}
                      height={200}
                    />
                  </animated.div>
                </Grid>
                <Grid size={{ sm: 12, lg: 3 }} flexGrow={1}>
                  <Typography variant="body2" gutterBottom align="center">
                    Nos techniciens ont-ils une photo ?
                  </Typography>
                  <animated.div style={chartSpring}>
                    <PieChart
                      colors={colors}
                      series={[
                        {
                          data: datasForChartTechnicians,
                          ...genericSeries,
                        },
                      ]}
                      sx={styleChart}
                      height={200}
                    />
                  </animated.div>
                </Grid>
              </Grid>
            ) : !error ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                minHeight="14.2rem"
              >
                <CircularProgress />
              </Box>
            ) : null}
          </CustomPaper>
        </Grid>
      </Grid>
      <Grid container sx={{ spacing: 1.5, mt: 0.5 }}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <CustomPaper>
            <Typography
              variant="h5"
              color="primary"
              gutterBottom
              align="left"
              mb={2}
            >
              Détail d'un utilisateur :
            </Typography>
            <Autocomplete
              id="search-autocomplete"
              options={
                searchResults.length
                  ? searchResults?.map((option) => {
                      const livestockCode =
                        option?.breeder?.farms[0]?.livestocks?.[0]?.codePerson;
                      return `${option?.displayName} --> Cheptel : ${
                        option?.userLogin
                      } / CPER : ${
                        livestockCode ? livestockCode : "Non renseigné"
                      }`;
                    })
                  : []
              }
              value={searchTerm}
              getOptionLabel={(option) => option}
              onInputChange={handleInputChange}
              isOptionEqualToValue={() => true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rechercher un utilisateur"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              renderOption={(props, option: string) => (
                <List style={{ cursor: "pointer" }} key={option}>
                  <ListItem onClick={() => onClickUser(option)}>
                    <ListItemText primary={option} />
                  </ListItem>
                </List>
              )}
            />
            {user && (
              <Grid container sx={{ spacing: 1.5, mt: 0.5 }}>
                <Grid size={{ xs: 12, lg: 12 }}>
                  <UserDetails
                    user={user}
                    postUpdatedUser={postUpdatedUser}
                    lastInvoiceUser={lastinvoicesUser}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 12 }}>
                  <Divider />
                </Grid>
                <Grid size={{ xs: 12, lg: 12 }}>
                  <UserConnections user={user} />
                </Grid>
              </Grid>
            )}
          </CustomPaper>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <PropspectsTable />
        </Grid>
      </Grid>
    </div>
  );
};

export default UsersPart;
